/** Same-origin JSON API client. Mutations carry the session CSRF token and an idempotency key (NFR-03). */
export interface ApiErrorBody {
  code: string;
  message: string;
  details?: { field?: string; message: string }[];
  recovery?: string;
  correlationId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: { field?: string; message: string }[];
  readonly recovery?: string;
  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.status = status;
    this.code = body.code;
    this.details = body.details ?? [];
    if (body.recovery) this.recovery = body.recovery;
  }
  fieldError(field: string) {
    return this.details.find((d) => d.field === field || d.field?.startsWith(`${field}.`))?.message;
  }
}

/**
 * A JSON client for one API surface. The member API lives under /api/v1; the external-intake popup under
 * /api/intake/v1 with its own CSRF token (JIRA-19).
 */
export function createApi(base: string) {
  let csrfToken: string | null = null;

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    init: { form?: FormData; operationKey?: string | undefined } = {},
  ): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (method !== 'GET') {
      if (csrfToken) headers['x-csrf-token'] = csrfToken;
      headers['idempotency-key'] = init.operationKey ?? crypto.randomUUID();
    }
    let payload: BodyInit | undefined;
    if (init.form) payload = init.form;
    else if (body !== undefined) {
      headers['content-type'] = 'application/json';
      payload = JSON.stringify(body);
    }
    const res = await fetch(`${base}${path}`, { method, headers, body: payload, credentials: 'same-origin' });
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    const json = text ? JSON.parse(text) : undefined;
    if (!res.ok) {
      const err = new ApiError(
        res.status,
        json?.error ?? { code: 'internal', message: res.statusText || 'Request failed' },
      );
      throw err;
    }
    return json as T;
  }

  return {
    setCsrfToken: (t: string | null) => {
      csrfToken = t;
    },
    get: <T>(path: string) => request<T>('GET', path),
    post: <T>(path: string, body?: unknown, operationKey?: string) =>
      request<T>('POST', path, body ?? {}, { operationKey }),
    put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
    patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
    del: <T>(path: string) => request<T>('DELETE', path),
    upload: <T>(path: string, form: FormData, operationKey?: string) =>
      request<T>('POST', path, undefined, { form, operationKey }),
  };
}

export type Api = ReturnType<typeof createApi>;

/** Same-origin member API. */
export const api = createApi('/api/v1');
export const setCsrfToken = api.setCsrfToken;

/** The external-intake popup's API: the platform's service account acting for one person (JIRA-19). */
export const intakeApi = createApi('/api/intake/v1');

/** Builds a query string, omitting empty values. */
export function qs(params: Record<string, string | number | boolean | undefined | null | string[]>) {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) continue;
    u.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}

/** Read-only, ticket-scoped third-party viewer; no member session API calls. */
export const viewerApi = createApi('/api/viewer/v1');
